// ABOUTME: Multi-step wizard for creating a CSV datasource by uploading files.
// ABOUTME: Steps: name -> file select with preview -> uploading/ingestion -> fix errors -> complete.

import { useState, useCallback, useRef } from 'react';
import { ArrowLeft, ArrowRight, Upload, X, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { getToken, getWorkspaceId, API_BASE_URL } from '@/services/api/client';
import { ColumnEditor, type InferredColumn, type ColumnMapping } from './ColumnEditor';

type WizardStep = 'name' | 'files' | 'uploading' | 'ingesting' | 'fixing' | 'complete';

interface CsvFileEntry {
  file: File;
  tableName: string;
  headers: string[];
  sampleRows: string[][];
  previewError?: string;
}

interface FileIngestionStatus {
  file_id: string;
  status: 'processing' | 'ready' | 'failed';
  error_message: string | null;
  error_category?: string | null;
  fixable?: boolean;
  table_name: string;
}

interface CsvUploadWizardProps {
  onComplete: () => void;
  onCancel: () => void;
  /** Override the default `/data-sources` prefix for all CSV API calls.
   *  When provided (e.g. `/v1/admin/workspaces/${wsId}/data-sources`),
   *  URLs are built as `${apiBaseUrl}/csv/...` with dsId after `/csv/`.
   *  When omitted the self-service URL structure is used unchanged. */
  apiBaseUrl?: string;
}

export function CsvUploadWizard({ onComplete, onCancel, apiBaseUrl }: CsvUploadWizardProps) {
  // URL builders that respect the different path structures between
  // self-service routes (/data-sources/{dsId}/csv/files/...) and
  // admin routes ({apiBaseUrl}/csv/{dsId}/files/...).
  const csvCreateUrl = apiBaseUrl
    ? `${API_BASE_URL}${apiBaseUrl}/csv`
    : `${API_BASE_URL}/data-sources/csv`;

  const csvFileUrl = (dsId: string, fileId: string, action: string) =>
    apiBaseUrl
      ? `${API_BASE_URL}${apiBaseUrl}/csv/${dsId}/files/${fileId}/${action}`
      : `${API_BASE_URL}/data-sources/${dsId}/csv/files/${fileId}/${action}`;

  const [step, setStep] = useState<WizardStep>('name');
  const [datasourceName, setDatasourceName] = useState('');
  const [fileEntries, setFileEntries] = useState<CsvFileEntry[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [ingestionError, setIngestionError] = useState<string | null>(null);
  const [createdDatasourceId, setCreatedDatasourceId] = useState<string | null>(null);
  const [fileStatuses, setFileStatuses] = useState<FileIngestionStatus[]>([]);
  const [schemaColumns, setSchemaColumns] = useState<InferredColumn[]>([]);
  const [fixingFileId, setFixingFileId] = useState<string | null>(null);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse CSV preview from file slice
  const parseFilePreview = useCallback((file: File): Promise<CsvFileEntry> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      const slice = file.slice(0, 8192);

      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (!text) {
          resolve({ file, tableName: sanitizeName(file.name), headers: [], sampleRows: [] });
          return;
        }

        const lines = text.split('\n').filter(Boolean);
        const delimiter = file.name.endsWith('.tsv') ? '\t' : ',';
        const headers = lines[0]?.split(delimiter).map((h) => h.trim().replace(/^"|"$/g, '')) ?? [];
        const sampleRows = lines.slice(1, 6).map((l) => l.split(delimiter).map((c) => c.trim().replace(/^"|"$/g, '')));

        resolve({
          file,
          tableName: sanitizeName(file.name),
          headers,
          sampleRows,
        });
      };

      reader.onerror = () => {
        resolve({
          file,
          tableName: sanitizeName(file.name),
          headers: [],
          sampleRows: [],
          previewError: 'Could not preview file',
        });
      };

      if (file.name.endsWith('.parquet')) {
        resolve({
          file,
          tableName: sanitizeName(file.name),
          headers: [],
          sampleRows: [],
          previewError: 'Parquet preview not available - schema will be inferred during ingestion',
        });
      } else {
        reader.readAsText(slice);
      }
    });
  }, []);

  const handleFilesSelected = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files).filter((f) => {
      const ext = f.name.toLowerCase();
      return ext.endsWith('.csv') || ext.endsWith('.tsv') || ext.endsWith('.parquet');
    });

    const entries = await Promise.all(fileArray.map(parseFilePreview));
    setFileEntries((prev) => [...prev, ...entries]);
  }, [parseFilePreview]);

  const removeFile = useCallback((index: number) => {
    setFileEntries((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateTableName = useCallback((index: number, name: string) => {
    setFileEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, tableName: name } : entry))
    );
  }, []);

  // Handle drag and drop
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length) {
      handleFilesSelected(e.dataTransfer.files);
    }
  };

  // Upload files via XHR for progress tracking
  const handleUpload = useCallback(async () => {
    setStep('uploading');
    setUploadProgress(0);
    setUploadError(null);

    try {
      const token = await getToken?.();
      const workspaceId = getWorkspaceId?.();

      const formData = new FormData();
      formData.append('name', datasourceName);
      formData.append(
        'table_names_json',
        JSON.stringify(fileEntries.map((e) => e.tableName))
      );
      fileEntries.forEach((entry) => {
        formData.append('files', entry.file);
      });

      const result = await new Promise<{ id: string }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try {
              const err = JSON.parse(xhr.responseText);
              reject(new Error(err.detail || `Upload failed: ${xhr.status}`));
            } catch {
              reject(new Error(`Upload failed: ${xhr.status}`));
            }
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.open('POST', csvCreateUrl);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        if (workspaceId) xhr.setRequestHeader('X-Workspace-ID', workspaceId);
        xhr.send(formData);
      });

      setCreatedDatasourceId(result.id);

      // Transition to ingestion polling step
      const fileIds = (result as { id: string; files: { id: string; table_name: string }[] }).files?.map(
        (f) => ({ file_id: f.id, status: 'processing' as const, error_message: null, table_name: f.table_name })
      ) ?? [];
      setFileStatuses(fileIds);
      setStep('ingesting');

      // Poll for ingestion completion
      if (fileIds.length > 0) {
        pollIngestionStatus(result.id, fileIds);
      } else {
        setStep('complete');
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    }
  }, [datasourceName, fileEntries, apiBaseUrl]);

  const pollIngestionStatus = useCallback(async (datasourceId: string, initialStatuses: FileIngestionStatus[]) => {
    const token = await getToken?.();
    const workspaceId = getWorkspaceId?.();
    const pendingFiles = new Set(initialStatuses.map((f) => f.file_id));
    const POLL_INTERVAL = 3000;
    const MAX_POLLS = 60; // 3 minutes max
    let polls = 0;

    const poll = async () => {
      if (pendingFiles.size === 0 || polls >= MAX_POLLS) return;
      polls++;

      const updatedStatuses = [...initialStatuses];
      let anyFailed = false;
      let allDone = true;

      for (const fileStatus of updatedStatuses) {
        if (fileStatus.status !== 'processing') continue;

        try {
          const resp = await fetch(
            csvFileUrl(datasourceId, fileStatus.file_id, 'status'),
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
              },
            }
          );

          if (resp.ok) {
            const data = await resp.json();
            fileStatus.status = data.status;
            fileStatus.error_message = data.error_message;
            fileStatus.error_category = data.error_category;
            fileStatus.fixable = data.fixable;

            if (data.status === 'ready') {
              pendingFiles.delete(fileStatus.file_id);
            } else if (data.status === 'failed') {
              pendingFiles.delete(fileStatus.file_id);
              anyFailed = true;
            } else {
              allDone = false;
            }
          }
        } catch {
          allDone = false;
        }
      }

      setFileStatuses([...updatedStatuses]);

      if (anyFailed) {
        const failedFiles = updatedStatuses.filter((f) => f.status === 'failed');
        const errorMessages = failedFiles.map(
          (f) => `${f.table_name}: ${f.error_message || 'Unknown error'}`
        );
        setIngestionError(errorMessages.join('\n'));
        return;
      }

      if (allDone && pendingFiles.size === 0) {
        setStep('complete');
        return;
      }

      setTimeout(poll, POLL_INTERVAL);
    };

    setTimeout(poll, POLL_INTERVAL);
  }, [apiBaseUrl]);

  const handleFixFile = useCallback(async (fileId: string) => {
    if (!createdDatasourceId) return;
    setIsLoadingSchema(true);
    setFixingFileId(fileId);

    try {
      const token = await getToken?.();
      const workspaceId = getWorkspaceId?.();

      const resp = await fetch(
        csvFileUrl(createdDatasourceId, fileId, 'schema'),
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
          },
        }
      );

      if (resp.ok) {
        const data = await resp.json();
        const failedStatus = fileStatuses.find((fs) => fs.file_id === fileId);
        setSchemaColumns(
          data.columns.map((col: { name: string; type: string }) => ({
            name: col.name,
            type: col.type,
            hasError: failedStatus?.error_category === 'column_type' || failedStatus?.error_category === 'column_name',
          }))
        );
        setStep('fixing');
      } else {
        setIngestionError('Failed to load file schema for editing.');
      }
    } catch {
      setIngestionError('Failed to load file schema for editing.');
    } finally {
      setIsLoadingSchema(false);
    }
  }, [createdDatasourceId, fileStatuses, apiBaseUrl]);

  const handleRetryWithSchema = useCallback(async (mappings: ColumnMapping[]) => {
    if (!createdDatasourceId || !fixingFileId) return;
    setIsRetrying(true);

    try {
      const token = await getToken?.();
      const workspaceId = getWorkspaceId?.();

      const resp = await fetch(
        csvFileUrl(createdDatasourceId, fixingFileId, 'retry-with-schema'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
          },
          body: JSON.stringify({
            columns: mappings.map((m) => ({
              original_name: m.originalName,
              original_type: m.originalType,
              new_name: m.newName,
              new_type: m.newType,
            })),
          }),
        }
      );

      if (resp.ok) {
        // Reset statuses and go back to ingesting
        setIngestionError(null);
        const updatedStatuses = fileStatuses.map((fs) =>
          fs.file_id === fixingFileId
            ? { ...fs, status: 'processing' as const, error_message: null, error_category: null, fixable: false }
            : fs
        );
        setFileStatuses(updatedStatuses);
        setStep('ingesting');
        pollIngestionStatus(createdDatasourceId, updatedStatuses);
      } else {
        const err = await resp.json().catch(() => ({ detail: 'Retry failed' }));
        setIngestionError(err.detail || 'Retry failed');
      }
    } catch {
      setIngestionError('Network error during retry.');
    } finally {
      setIsRetrying(false);
    }
  }, [createdDatasourceId, fixingFileId, fileStatuses, pollIngestionStatus, apiBaseUrl]);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {step !== 'complete' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={step === 'name' ? onCancel : () => setStep(step === 'files' ? 'name' : 'files')}
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <h2 className="text-xl font-bold text-surface-900">
            {step === 'name' && 'Name your datasource'}
            {step === 'files' && 'Upload files'}
            {step === 'uploading' && 'Uploading...'}
            {step === 'ingesting' && 'Processing...'}
            {step === 'fixing' && 'Fix column issues'}
            {step === 'complete' && 'Datasource created'}
          </h2>
        </div>
        <button onClick={onCancel} className="p-1 rounded-sm hover:bg-surface-100" aria-label="Close">
          <X className="w-5 h-5 text-surface-500" />
        </button>
      </div>

      {/* Step: Name */}
      {step === 'name' && (
        <div className="space-y-4">
          <div>
            <label htmlFor="ds-name" className="block text-sm font-medium text-surface-700 mb-1">
              Datasource name
            </label>
            <input
              id="ds-name"
              type="text"
              value={datasourceName}
              onChange={(e) => setDatasourceName(e.target.value)}
              placeholder="e.g., Q1 Sales Data"
              className="w-full px-3 py-2 border border-surface-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => setStep('files')}
              disabled={!datasourceName.trim()}
            >
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step: File Select + Preview */}
      {step === 'files' && (
        <div className="space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
              isDragging
                ? 'border-primary-500 bg-primary-50'
                : 'border-surface-300 hover:border-surface-400 hover:bg-surface-50'
            }`}
          >
            <Upload className="w-8 h-8 mx-auto text-surface-400 mb-2" />
            <p className="text-sm text-surface-600">
              Drag & drop files here, or click to browse
            </p>
            <p className="text-xs text-surface-400 mt-1">
              Supports .csv, .tsv, .parquet
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".csv,.tsv,.parquet"
              onChange={(e) => e.target.files && handleFilesSelected(e.target.files)}
              className="hidden"
              aria-label="Upload CSV files"
            />
          </div>

          {/* File list with preview */}
          {fileEntries.length > 0 && (
            <div className="space-y-3">
              {fileEntries.map((entry, idx) => (
                <div key={idx} className="border border-surface-200 rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-surface-500" />
                      <span className="text-sm font-medium text-surface-900">{entry.file.name}</span>
                      <span className="text-xs text-surface-400">
                        ({(entry.file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <button
                      onClick={() => removeFile(idx)}
                      className="p-1 rounded-sm hover:bg-surface-100"
                      aria-label="Remove file"
                    >
                      <X className="w-4 h-4 text-surface-400" />
                    </button>
                  </div>

                  {/* Table name input */}
                  <div className="mb-2">
                    <label className="block text-xs text-surface-500 mb-1">Table name</label>
                    <input
                      type="text"
                      value={entry.tableName}
                      onChange={(e) => updateTableName(idx, e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-surface-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  {/* Column preview */}
                  {entry.previewError ? (
                    <p className="text-xs text-surface-400 italic">{entry.previewError}</p>
                  ) : entry.headers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-surface-200">
                            {entry.headers.map((h, i) => (
                              <th key={i} className="px-2 py-1 text-left text-surface-500 font-medium">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {entry.sampleRows.slice(0, 3).map((row, ri) => (
                            <tr key={ri} className="border-b border-surface-100">
                              {row.map((cell, ci) => (
                                <td key={ci} className="px-2 py-1 text-surface-700 truncate max-w-[150px]">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleUpload}
              disabled={fileEntries.length === 0}
            >
              Upload {fileEntries.length} file{fileEntries.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      )}

      {/* Step: Uploading */}
      {step === 'uploading' && (
        <div className="text-center py-8 space-y-4">
          {uploadError ? (
            <>
              <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
              <p className="text-red-600 font-medium">Upload failed</p>
              <p className="text-sm text-surface-500">{uploadError}</p>
              <Button onClick={() => { setUploadError(null); handleUpload(); }}>
                Retry
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-primary-500 animate-spin" />
              <p className="text-surface-700 font-medium">
                Uploading and processing files...
              </p>
              <div className="w-full max-w-xs mx-auto bg-surface-200 rounded-full h-2">
                <div
                  className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-surface-500">{uploadProgress}%</p>
            </>
          )}
        </div>
      )}

      {/* Step: Ingesting (polling for table creation) */}
      {step === 'ingesting' && (
        <div className="text-center py-8 space-y-4">
          {ingestionError ? (
            <>
              <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
              <p className="text-red-600 font-medium">Ingestion failed</p>
              <div className="text-sm text-surface-500 text-left max-w-md mx-auto bg-surface-50 rounded-md p-3 space-y-1">
                {ingestionError.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
              <div className="flex justify-center gap-2">
                {fileStatuses.some((fs) => fs.fixable) && (
                  <Button
                    onClick={() => {
                      const fixableFile = fileStatuses.find((fs) => fs.fixable && fs.status === 'failed');
                      if (fixableFile) handleFixFile(fixableFile.file_id);
                    }}
                    disabled={isLoadingSchema}
                    data-testid="fix-and-retry-btn"
                  >
                    {isLoadingSchema ? 'Loading schema...' : 'Fix and retry'}
                  </Button>
                )}
                <Button variant="ghost" onClick={onCancel}>Close</Button>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-primary-500 animate-spin" />
              <p className="text-surface-700 font-medium">
                Processing files...
              </p>
              <div className="max-w-sm mx-auto space-y-2">
                {fileStatuses.map((fs) => (
                  <div key={fs.file_id} className="flex items-center gap-2 text-sm">
                    {fs.status === 'processing' && <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />}
                    {fs.status === 'ready' && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {fs.status === 'failed' && <AlertCircle className="w-4 h-4 text-red-500" />}
                    <span className="text-surface-700">{fs.table_name}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-surface-400">This may take a minute...</p>
            </>
          )}
        </div>
      )}

      {/* Step: Fixing (column editor) */}
      {step === 'fixing' && (
        <div className="py-4">
          <ColumnEditor
            columns={schemaColumns}
            onRetry={handleRetryWithSchema}
            onCancel={() => {
              setStep('ingesting');
            }}
            isRetrying={isRetrying}
          />
          {ingestionError && (
            <div className="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded">
              {ingestionError}
            </div>
          )}
        </div>
      )}

      {/* Step: Complete */}
      {step === 'complete' && (
        <div className="text-center py-8 space-y-4">
          <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
          <p className="text-surface-900 font-semibold text-lg">
            Datasource created
          </p>
          <p className="text-surface-500">
            Your files will be available shortly.
          </p>
          <div className="flex justify-center gap-3 mt-6">
            <Button variant="ghost" onClick={onComplete}>
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function sanitizeName(filename: string): string {
  let name = filename.replace(/\.(csv|tsv|parquet|xlsx|xls)$/i, '');
  name = name.toLowerCase().replace(/[\s\-\.]+/g, '_').replace(/[^a-z0-9_]/g, '');
  name = name.replace(/^[0-9]+/, '');
  if (!name) name = 'table';
  return name.slice(0, 128);
}
